import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CTAButton from '@components/CTAButton';
import { createSmartPractice } from '@containers/Admin/containers/Builder/service/skill';
import {
  ErrorText,
  Field,
  FieldLabel,
  LessonItem,
  LessonList,
  ResultList,
  Results,
  TextInput,
} from './styles';
import {
  FormPopupActions,
  FormPopupSurface,
  FormPopupTitle,
} from '../../components/FormPopup/styles';

const MAX_REPEAT_COUNT = 20;

const SmartPractice = ({
  skillId, lessons, onClose, onCreated,
}) => {
  const { t } = useTranslation();
  const [practiceName, setPracticeName] = useState('');
  const [repeatCount, setRepeatCount] = useState(1);
  const [selectedLessonIds, setSelectedLessonIds] = useState(
    lessons.map(({ id }) => id),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const toggleLesson = (lessonId) => {
    setSelectedLessonIds((currentIds) => (
      currentIds.includes(lessonId)
        ? currentIds.filter((id) => id !== lessonId)
        : [...currentIds, lessonId]
    ));
  };

  const submit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const response = await createSmartPractice({
        skillId,
        practiceName: practiceName.trim(),
        repeatCount,
        lessonIds: selectedLessonIds,
      });
      const creationResult = response && response.data.createSmartPractice;
      if (!creationResult) throw new Error(t('SmartPractice.errors.creationFailed'));
      setResult(creationResult);
      if (creationResult.practiceId && onCreated) onCreated(creationResult);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = practiceName.trim()
    && selectedLessonIds.length
    && Number.isInteger(repeatCount)
    && repeatCount >= 1
    && repeatCount <= MAX_REPEAT_COUNT;

  return (
    <FormPopupSurface>
      <FormPopupTitle>{t('SmartPractice.title')}</FormPopupTitle>
      {!result ? (
        <>
          <Field>
            <FieldLabel htmlFor="smartPracticeName">
              {t('SmartPractice.practiceName')}
            </FieldLabel>
            <TextInput
              id="smartPracticeName"
              value={practiceName}
              onChange={(event) => setPracticeName(event.target.value)}
              disabled={submitting}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="smartPracticeRepeatCount">
              {t('SmartPractice.repeatCount')}
            </FieldLabel>
            <TextInput
              id="smartPracticeRepeatCount"
              type="number"
              min="1"
              max={MAX_REPEAT_COUNT}
              value={repeatCount}
              onChange={(event) => setRepeatCount(Number(event.target.value))}
              disabled={submitting}
            />
          </Field>
          <Field>
            <FieldLabel>{t('SmartPractice.lessons')}</FieldLabel>
            <LessonList>
              {lessons.map((lesson) => (
                <LessonItem key={lesson.id}>
                  <input
                    type="checkbox"
                    checked={selectedLessonIds.includes(lesson.id)}
                    onChange={() => toggleLesson(lesson.id)}
                    disabled={submitting}
                  />
                  <span>{lesson.name}</span>
                </LessonItem>
              ))}
            </LessonList>
          </Field>
          {error && <ErrorText>{error}</ErrorText>}
          <FormPopupActions>
            <CTAButton cancel onClick={onClose} disabled={submitting}>
              {t('Actions.Instance.cancel')}
            </CTAButton>
            <CTAButton onClick={submit} disabled={!isValid || submitting}>
              {submitting
                ? t('SmartPractice.creating')
                : t('SmartPractice.createPractice')}
            </CTAButton>
          </FormPopupActions>
        </>
      ) : (
        <Results>
          <strong>
            {result.practiceId
              ? t('SmartPractice.createdSuccessfully', { name: result.practiceName })
              : t('SmartPractice.noPracticeCreated')}
          </strong>
          <ResultList>
            <li>{t('SmartPractice.results.created', { count: result.createdExercises.length })}</li>
            <li>{t('SmartPractice.results.reused', { count: result.reusedExercises.length })}</li>
            <li>{t('SmartPractice.results.skipped', { count: result.skippedLessons.length })}</li>
            <li>{t('SmartPractice.results.failed', { count: result.failedLessons.length })}</li>
          </ResultList>
          {[...result.skippedLessons, ...result.failedLessons].map((lesson) => (
            <ErrorText key={`${lesson.lessonId}-${lesson.reason}`}>
              {`${lesson.lessonName}: ${lesson.reason}`}
            </ErrorText>
          ))}
          <FormPopupActions>
            <CTAButton onClick={onClose}>{t('SmartPractice.done')}</CTAButton>
          </FormPopupActions>
        </Results>
      )}
    </FormPopupSurface>
  );
};

SmartPractice.propTypes = {
  skillId: PropTypes.string.isRequired,
  lessons: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};

export default SmartPractice;
